"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import type { WeddingWelcomeSectionProps } from "@/lib/sections/wedding-welcome.types";

type TimeRemaining = {
  total: number;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
};

const EMPTY_TIME_REMAINING: TimeRemaining = {
  total: 0,
  days: 0,
  hours: 0,
  minutes: 0,
  seconds: 0,
};

function getTimeRemaining(targetDate: Date) {
  const total = Math.max(targetDate.getTime() - new Date().getTime(), 0);

  const seconds = Math.floor((total / 1000) % 60);
  const minutes = Math.floor((total / 1000 / 60) % 60);
  const hours = Math.floor((total / (1000 * 60 * 60)) % 24);
  const days = Math.floor(total / (1000 * 60 * 60 * 24));

  return { total, days, hours, minutes, seconds };
}

export function WeddingWelcomeSection({
  names,
  weddingDate,
  title,
  subtitle,
  themeLabel,
  themeColors,
  countdownText,
  labels = {},
  locale = "fr-FR",
  images = {},
}: WeddingWelcomeSectionProps) {
  const targetDate = new Date(weddingDate);
  const [time, setTime] = useState<TimeRemaining>(EMPTY_TIME_REMAINING);
  const isFrench = locale.startsWith("fr");
  const isEnglish = locale.startsWith("en");
  const defaults = isFrench
    ? {
        title: "Bienvenue",
        subtitle: "À notre mariage",
        countdownText: "Avant notre grand jour, il reste encore",
        days: "Jours",
        hours: "Heures",
        minutes: "Minutes",
        seconds: "Secondes",
      }
    : isEnglish
      ? {
          title: "Welcome",
          subtitle: "To our wedding",
          countdownText: "Until our big day, there are still",
          days: "Days",
          hours: "Hours",
          minutes: "Minutes",
          seconds: "Seconds",
        }
      : {
          title: "Willkommen",
          subtitle: "Zu unserer Hochzeit",
          countdownText: "Bis zu unserem grossen Tag dauert es noch",
          days: "Tage",
          hours: "Stunden",
          minutes: "Minuten",
          seconds: "Sekunden",
        };

  useEffect(() => {
    const updateCountdown = () => {
      setTime(getTimeRemaining(targetDate));
    };

    updateCountdown();
    const timer = window.setInterval(updateCountdown, 1000);

    return () => window.clearInterval(timer);
  }, [weddingDate]);

  const [firstName, secondName] = names.split("&").map((name) => name.trim());

  const formattedDate = targetDate.toLocaleDateString(locale, {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return (
    <section className="bg-[#fffdf8] px-6 py-24 text-center">
      <div className="mx-auto max-w-3xl space-y-10">
        <div className="space-y-6">
          <h1 className="font-serif text-4xl text-[#075e4f] md:text-5xl">
            {title ?? defaults.title}
          </h1>

          <div className="relative mx-auto max-w-md border-y border-[#b6924a]/60 px-8 py-5 text-[#075e4f] sm:px-12">
            <BotanicalAccent className="left-0 -scale-x-100" />
            <BotanicalAccent className="right-0" />

            <p className="font-serif text-lg italic text-[#315f56]">
              {subtitle ?? defaults.subtitle}
            </p>
            <p className="mt-3 text-[0.65rem] font-medium uppercase tracking-[0.28em] text-[#96752f]">
              {themeLabel}
            </p>
            <p className="mt-1.5 font-serif text-base tracking-wide sm:text-lg">
              {themeColors}
            </p>
          </div>
        </div>

        <div className="flex flex-col items-center justify-center gap-1">
          <span className="font-script text-[2.6rem] leading-none md:text-[3.2rem]">
            {firstName}
          </span>

          {images.rings ? (
            <Image
              src={images.rings}
              alt=""
              width={64}
              height={64}
              className="opacity-70"
            />
          ) : null}

          <span className="font-script text-[2.6rem] leading-none md:text-[3.2rem]">
            {secondName}
          </span>
        </div>

        <p className="text-3xl font-serif capitalize text-amber-600 md:text-4xl">
          {formattedDate}
        </p>

        <div className="space-y-4">
          <p className="text-sm text-gray-600">{countdownText ?? defaults.countdownText}</p>

          <div className="grid grid-cols-2 gap-6 sm:flex sm:justify-center">
            <CountdownItem value={time.days} label={labels.days ?? defaults.days} />
            <CountdownItem value={time.hours} label={labels.hours ?? defaults.hours} />
            <CountdownItem value={time.minutes} label={labels.minutes ?? defaults.minutes} />
            <CountdownItem value={time.seconds} label={labels.seconds ?? defaults.seconds} />
          </div>
        </div>
      </div>
    </section>
  );
}

function BotanicalAccent({ className }: { className: string }) {
  return (
    <span
      aria-hidden="true"
      className={`absolute top-1/2 hidden h-12 w-8 -translate-y-1/2 text-[#b6924a]/55 sm:block ${className}`}
    >
      <span className="absolute left-3 top-1 h-10 w-px -rotate-[18deg] bg-current" />
      <span className="absolute left-1 top-3 h-2 w-4 -rotate-[28deg] rounded-[100%_0] border-t border-current" />
      <span className="absolute left-3 top-6 h-2 w-4 rotate-[22deg] rounded-[0_100%] border-t border-current" />
    </span>
  );
}

function CountdownItem({ value, label }: { value: number; label: string }) {
  return (
    <div className="min-w-[60px] text-center">
      <div className="text-3xl font-serif md:text-4xl">
        {String(value).padStart(2, "0")}
      </div>
      <div className="text-xs uppercase tracking-wide text-gray-500">{label}</div>
    </div>
  );
}
